import { useState } from 'react';
import { Pressable, useColorScheme } from 'react-native';
import { YStack } from 'tamagui';

import { Text } from '@/components/text';
import { Ellipsis as MoreHorizontalIcon } from 'lucide-react-native';
import { Sheet } from '@/components/sheet';
import { darkColors, lightColors } from '@/theme/tokens.generated';
import { hit } from '@/lib/hit-slop';

export type OverflowAction = {
  label: string;
  onPress: () => void;
  // Destructive actions render in $destructive -- this project's own
  // token-by-intent rule (one action role, one variant, everywhere).
  destructive?: boolean;
};

// Issue #291: Block/Report used to render as two equal-weight plain-
// text links directly under a thread/post's meta -- both a token-by-
// intent violation (this project's own non-negotiable #1: destructive
// actions get `$destructive`, everywhere, same variant) AND an
// emotional problem the issue itself names: ambient threat framing on
// a neutral read, on the app's most-read screen. Moved into a `more-
// horizontal` overflow on the author row instead -- present, reachable,
// but no longer competing with the actual content for the eye on every
// single post.
//
// Issue #64 generalized that component into this one: the chat header
// needs the same control with a different action set (Unmatch), and a
// second near-identical menu hardcoding its own two pressables is how
// the two drift apart. Callers now describe their actions as data.
//
// Uses the existing Sheet primitive (sheet.tsx), not a bespoke
// dropdown/popover -- that component's own doc comment already names
// "a future confirmation flow (e.g. a Block/Report confirm-before-
// submit step)" as its anticipated first real use. The actions
// themselves still navigate to their existing dedicated screens
// (block-user/index.tsx, report-target/index.tsx) -- those already
// ARE the confirm step (block-user's own doc comment: "Single
// confirmation only... no additional 'are you sure?' step on top",
// issue #17's explicit no-dark-patterns rule) -- this menu is only the
// entry point, styled by intent, not a second confirmation layer.
export function OverflowMenu({ actions, label = 'More options' }: { actions: OverflowAction[]; label?: string }) {
  const [open, setOpen] = useState(false);
  const colorScheme = useColorScheme();
  const colors = colorScheme === 'dark' ? darkColors : lightColors;

  return (
    <>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={label}
        {...hit(10)}
        onPress={() => setOpen(true)}
        style={{ width: 44, height: 44, alignItems: 'center', justifyContent: 'center' }}
      >
        <MoreHorizontalIcon size={20} strokeWidth={1.75} color={colors.textTertiary} />
      </Pressable>
      {/* snapPoints -- verified live against production (thread-detail's
          own composer sheet had the identical issue): without it, the
          sheet's default left a large empty area below a short action
          list instead of a tight fit. Sized per action so the sheet hugs
          its content.
          Anchored on 2 actions, not 0: 25 is the value verified live
          against production for the two-action menus this generalizes
          (thread-detail's Block/Report), so that case must keep it. The
          first draft sized from zero actions and silently made both of
          those sheets taller -- more empty space under the rows, the exact
          defect this snap point exists to prevent. */}
      <Sheet open={open} onOpenChange={setOpen} snapPoints={[25 + (actions.length - 2) * 8]}>
        <YStack>
          {actions.map((action) => (
            <Pressable
              key={action.label}
              accessibilityRole="button"
              style={{ minHeight: 44, justifyContent: 'center' }}
              onPress={() => {
                setOpen(false);
                action.onPress();
              }}
            >
              <Text
                fontFamily="$body"
                color={action.destructive ? '$destructive' : '$color'}
                fontSize="$body"
                lineHeight="$body"
                fontWeight="$medium"
              >
                {action.label}
              </Text>
            </Pressable>
          ))}
        </YStack>
      </Sheet>
    </>
  );
}
